import { BREAKPOINT_TABLET_PORTRAIT } from 'src/constants'
import {
  FONT_SIZES,
  LONG_TEXT_CHARACTER_COUNT,
  LONG_TEXT_MODIFIER,
  WIDTHS,
} from './Preference.constants'

interface AdjustSizesProps {
  width: number
  wrapperRef: React.RefObject<HTMLDivElement>
  headingRef: React.RefObject<HTMLHeadingElement>
  iconRef: React.RefObject<HTMLDivElement>
  contentRef: React.RefObject<HTMLDivElement>
}

const round = (value: number): number => Math.round(value * 100) / 100

export const adjustSizes = ({
  width,
  wrapperRef,
  headingRef,
  iconRef,
  contentRef,
}: AdjustSizesProps): void => {
  const windowFormat =
    width >= BREAKPOINT_TABLET_PORTRAIT ? 'desktop' : 'mobile'

  const comparableMaxWidth = WIDTHS[windowFormat].max
  const comparableMinWidth = WIDTHS[windowFormat].min
  const wrapperWidth = wrapperRef.current!.clientWidth
  const isMaxSize = wrapperWidth >= comparableMaxWidth
  const isMinSize = wrapperWidth <= comparableMinWidth
  const sideModifier =
    contentRef.current!.innerText?.length > LONG_TEXT_CHARACTER_COUNT
      ? LONG_TEXT_MODIFIER
      : 1

  const maxHeadingFontSize = FONT_SIZES.heading[windowFormat].max
  const maxContentFontSize = round(
    FONT_SIZES.content[windowFormat].max * sideModifier
  )
  const minContentFontSize = round(
    FONT_SIZES.content[windowFormat].min * sideModifier
  )

  // If the wrapper is at its maximum or minimum size, we need to clamp
  // the font sizes to their respective sizes
  if (isMaxSize) {
    headingRef.current!.style.fontSize = `${maxHeadingFontSize}px`
    iconRef.current!.style.width = `${maxHeadingFontSize}px`
    iconRef.current!.style.height = `${maxHeadingFontSize}px`
    contentRef.current!.style.fontSize = `${maxContentFontSize}px`
    return
  }

  if (isMinSize) {
    headingRef.current!.style.fontSize = `${FONT_SIZES.heading[windowFormat].min}px`
    iconRef.current!.style.width = `${FONT_SIZES.heading[windowFormat].min}px`
    iconRef.current!.style.height = `${FONT_SIZES.heading[windowFormat].min}px`
    contentRef.current!.style.fontSize = `${minContentFontSize}px`
    return
  }

  // When the wrapper is transitioning between its maximum and minimum sizes,
  // we need to calculate the font sizes relative to the wrapper's ratio of
  // the current width to the maximum width to ensure a smooth transition
  // between the two sizes for the heading, icon, and content
  const wrapperWidthRatio = wrapperWidth / comparableMaxWidth
  const headingFontSize = Math.max(
    round(maxHeadingFontSize * wrapperWidthRatio),
    FONT_SIZES.heading[windowFormat].min
  )
  const contentFontSize = Math.max(
    round(maxContentFontSize * wrapperWidthRatio),
    minContentFontSize
  )

  headingRef.current!.style.fontSize = `${headingFontSize}px`
  iconRef.current!.style.width = `${headingFontSize}px`
  iconRef.current!.style.height = `${headingFontSize}px`
  contentRef.current!.style.fontSize = `${contentFontSize}px`
}
